import React from 'react'

import { EditableSection } from 'features/views/attributes/editable/section/EditableSection'
import { WidgetComponent } from 'features/views/LayoutEditor/types'

import { Box } from 'ui/components/Box'
import { Body } from 'ui/components/Text'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { Attribute } from './attributes/Attribute'
import { HighlightsWidgetAttributeItem } from './attributes/types'
import { useHighlightsWidgetItemListState } from './hooks/useHighlightsWidgetItemListState'
import { useHighlightsWidgetState } from './hooks/useHighlightsWidgetState'
import { HighlightsWidgetType } from './types'

import {
    HighlightsWidgetItemListStyles,
    HighlightsWidgetItemRowStyles,
    HighlightsWidgetItemWrapperStyles,
    HighlightsWidgetStyle,
} from './HighlightsWidget.css'

type HighlightsWidgetProps = {}

const HighlightsWidgetComponent: WidgetComponent<HighlightsWidgetType, HighlightsWidgetProps> = ({
    widget,
    isEditing,
}) => {
    const {
        hasRecord,
        isLoadingSlow,
        hasItems,
        style,
        visibleItems,
        showItemLabel,
        showItemIcon,
        editableFields,
    } = useHighlightsWidgetState({
        widget,
    })

    const verticalPadding = {
        mobile: 'l',
        tablet: style === 'boxed' ? '4xl' : 'xl',
    } as const

    if (!hasRecord && !isLoadingSlow) return null
    if (!hasItems && !isEditing) return null

    if (!hasItems && isEditing) {
        return (
            <Box flex column gap="m" py={verticalPadding}>
                <Body color="textWeakest">No fields selected</Body>
            </Box>
        )
    }

    return (
        <Box py={verticalPadding} width="full" className={HighlightsWidgetStyle}>
            <EditableSection width="full" fields={editableFields}>
                <HighlightsWidgetItemList
                    items={visibleItems}
                    isLoading={isLoadingSlow}
                    style={style}
                    showItemLabel={showItemLabel}
                    showItemIcon={showItemIcon}
                />
            </EditableSection>
        </Box>
    )
}

export const HighlightsWidget = React.memo(HighlightsWidgetComponent)

type HighlightsWidgetItemListProps = {
    items: HighlightsWidgetAttributeItem[]
    isLoading?: boolean
    style?: HighlightsWidgetType['attrs']['style']
    showItemLabel?: boolean
    showItemIcon?: boolean
}

const HighlightsWidgetItemList: React.FC<HighlightsWidgetItemListProps> = ({
    items,
    isLoading,
    style,
    showItemLabel,
    showItemIcon,
}) => {
    const { columnCount, ref, itemsByRow } = useHighlightsWidgetItemListState({
        items,
    })

    const size = useResponsiveValue({
        mobile: 'm',
        tablet: style === 'boxed' ? 'l' : 'm',
    })

    return (
        <Box ref={ref} className={HighlightsWidgetItemListStyles.styleFunction({ style })}>
            {itemsByRow.map((row, rowIdx) => {
                return (
                    <Box
                        key={rowIdx}
                        className={HighlightsWidgetItemRowStyles.styleFunction({ style })}
                    >
                        {row.map((item, idx) => {
                            const currentColumn = idx % columnCount
                            const isLastColumn = currentColumn === columnCount - 1

                            return (
                                <Box
                                    key={item.sid}
                                    className={HighlightsWidgetItemWrapperStyles.styleFunction({
                                        style,
                                        isLastColumn,
                                    })}
                                >
                                    <Attribute
                                        key={item.sid}
                                        size={size}
                                        item={item}
                                        isLoading={isLoading}
                                        displayStyle={style}
                                        showLabel={showItemLabel}
                                        showIcon={showItemIcon}
                                    />
                                </Box>
                            )
                        })}
                    </Box>
                )
            })}
        </Box>
    )
}
